/*
 * SeoMistakesCheck
 * The article's five common SEO mistakes, each marked "True for us" / "Not us" / "Not sure".
 *
 * Rules:
 * - "True for us" -> listed under "Fix next" with its specific fix and a link.
 * - "Not sure"    -> listed under "Check first" with the same fix and link
 *                    (an unknown is something to verify, not a pass).
 * - "Not us"      -> nothing shown for that mistake.
 * - Results update as the reader answers. When all five are "Not us", the reader is pointed
 *   to the free audit to find less obvious gaps.
 * No scores, benchmarks, tracking, storage or network calls.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";

type Answer = "true" | "no" | "unsure";

type Mistake = {
  id: string;
  text: string;
  fix: string;
  link: { href: string; text: string };
};

const MISTAKES: Mistake[] = [
  {
    id: "broad",
    text: "Our keywords are mostly broad terms, not the specific phrases clients type.",
    fix: "Build pages and posts around specific, urgent searches (like “what to do after a hit and run”) alongside the broad terms.",
    link: { href: "/practice-areas", text: "SEO by practice area" },
  },
  {
    id: "thin",
    text: "Some of our practice-area pages are thin: they target a keyword but don't really help the reader.",
    fix: "Rewrite them to answer what a worried reader actually asks: what to do next, what it might cost, what happens in a consultation.",
    link: { href: "/services/legal-content-writing", text: "Legal content writing" },
  },
  {
    id: "technical",
    text: "Nobody has checked our technical SEO (crawling, indexing, speed, broken links, structured data).",
    fix: "Run a technical review and fix anything stopping pages from being crawled, indexed or loading quickly.",
    link: { href: "/services/technical-seo-for-law-firms", text: "Technical SEO for law firms" },
  },
  {
    id: "intake",
    text: "We treat a new lead as the finish line; there's no set process for following up fast.",
    fix: "Agree who answers new inquiries, how quickly, and track what happens to each one through to a signed client.",
    link: { href: "/blog/how-law-firms-turn-seo-traffic-into-new-clients", text: "How to turn SEO traffic into clients" },
  },
  {
    id: "patience",
    text: "We've stopped (or are about to stop) SEO because it didn't work in the first few months.",
    fix: "Before stopping, check the early signs (pages indexed, rankings moving, organic inquiries) rather than judging on signed cases alone.",
    link: { href: "/services/law-firm-marketing-analytics", text: "Analytics & reporting" },
  },
];

const OPTIONS: { value: Answer; label: string }[] = [
  { value: "true", label: "True for us" },
  { value: "no", label: "Not us" },
  { value: "unsure", label: "Not sure" },
];

export default function SeoMistakesCheck() {
  const [answers, setAnswers] = useState<Record<string, Answer | undefined>>({});

  const answered = MISTAKES.filter((m) => answers[m.id]).length;
  const fixNext = MISTAKES.filter((m) => answers[m.id] === "true");
  const checkFirst = MISTAKES.filter((m) => answers[m.id] === "unsure");
  const allClear = answered === MISTAKES.length && fixNext.length === 0 && checkFirst.length === 0;

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  const renderList = (title: string, items: Mistake[]) =>
    items.length > 0 && (
      <div className="mt-3">
        <p className="text-sm font-semibold">{title}</p>
        <ul className="mt-2 space-y-3">
          {items.map((m) => (
            <li key={m.id} className="rounded-md border border-border bg-card p-3 text-sm">
              <p className="font-medium">{m.text}</p>
              <p className="mt-1 text-muted-foreground">{m.fix}</p>
              <Link to={m.link.href} className={`mt-1 inline-block font-medium underline underline-offset-4 ${focusRing}`}>
                {m.link.text} →
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Is your firm making these SEO mistakes?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Mark each one honestly. You’ll get a short fix list, with nothing saved or sent anywhere.
      </p>

      <ol className="mt-5 space-y-4">
        {MISTAKES.map((m, i) => {
          const labelId = `seo-mistake-${m.id}`;
          return (
            <li key={m.id} className="rounded-lg border border-border p-4">
              <p id={labelId} className="text-sm font-medium">
                {i + 1}. {m.text}
              </p>
              <div role="group" aria-labelledby={labelId} className="mt-3 flex flex-wrap gap-2">
                {OPTIONS.map((o) => {
                  const pressed = answers[m.id] === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={pressed}
                      onClick={() => setAnswers((prev) => ({ ...prev, [m.id]: o.value }))}
                      className={`rounded-md border px-4 py-1.5 text-sm ${focusRing} ${
                        pressed
                          ? "border-border bg-primary text-primary-foreground"
                          : "border-input bg-card text-card-foreground"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        <p className="text-sm text-muted-foreground">
          {answered} of {MISTAKES.length} answered
        </p>
        {answered === 0 && <p className="mt-1 text-sm">Your fix list will appear here as you answer.</p>}
        {renderList("Fix next", fixNext)}
        {renderList("Check first (you weren’t sure)", checkFirst)}
        {allClear && (
          <p className="mt-2 text-sm">
            None of the common mistakes apply. The gaps left are usually less obvious ones.{" "}
            <Link to="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
              Get a free SEO audit →
            </Link>
          </p>
        )}
        {answered > 0 && answered < MISTAKES.length && fixNext.length === 0 && checkFirst.length === 0 && (
          <p className="mt-1 text-sm">Nothing to fix so far. Keep going.</p>
        )}
      </div>
    </section>
  );
}
